import styles from './Sparkline.module.css'

/**
 * Sparkline — a filled polyline over a numeric series. Scales to the
 * container width (preserveAspectRatio:none); a non-scaling stroke keeps
 * the line crisp under that stretch. Renders nothing for fewer than two
 * finite points.
 *
 * Extracted from MetricCard's graph cardtype so it can be reused anywhere
 * a small trend squiggle is needed (e.g. the Home tab's stat strip).
 *
 * Props:
 *   values {number[]} — the series to plot
 *   color  {string}   — stroke/fill color (CSS color value). Defaults to
 *                       the semantic blue MetricCard has always used, so
 *                       existing graph cards render identically.
 */
export default function Sparkline({ values, color = 'var(--color-semantic-blue)' }) {
  const nums = (Array.isArray(values) ? values : []).map(Number).filter(Number.isFinite)
  if (nums.length < 2) return null

  const W = 100, H = 32, PAD = 3
  const min = Math.min(...nums)
  const max = Math.max(...nums)
  const span = max - min || 1
  const stepX = (W - PAD * 2) / (nums.length - 1)
  const pts = nums.map((v, i) => {
    const x = PAD + i * stepX
    const y = PAD + (H - PAD * 2) * (1 - (v - min) / span)
    return `${x.toFixed(2)},${y.toFixed(2)}`
  })
  const line = pts.join(' ')
  const area = `${PAD},${H} ${line} ${(W - PAD).toFixed(2)},${H}`

  return (
    <svg className={styles.sparkline} viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" aria-hidden="true">
      <polygon className={styles.sparkArea} points={area} style={{ fill: color }} />
      <polyline className={styles.sparkLine} points={line} style={{ stroke: color }} vectorEffect="non-scaling-stroke" />
    </svg>
  )
}
